import { Link } from "react-router-dom";
import {
  ArrowRight, BadgeCheck, ClipboardCheck, Droplets, FileCheck, Mail, Sun,
  ShieldCheck, Wind, Zap,
} from "lucide-react";
import { FadeUp, Overline, SectionHead } from "@/components/site/Reveal";

const ELECTRICIAN_IMG = "/api/files/melwest-eco-energy/brand/installer-electrician.jpeg";
const PLUMBER_IMG = "/api/files/melwest-eco-energy/brand/installer-plumber.jpeg";
const FRIDGE_IMG = "/api/files/melwest-eco-energy/brand/installer-fridge-tech.jpeg";

const TRADES = [
  {
    img: ELECTRICIAN_IMG,
    alt: "Animated illustration of a licensed electrician working on a residential switchboard",
    icon: Zap,
    iconColor: "text-terra",
    title: "Licensed Electricians",
    licence: "Licensed by Energy Safe Victoria (ESV)",
    copy: "A-Grade electricians handle every wire we quote — switchboard upgrades, solar connections, isolators and heat pump circuits.",
    bullets: [
      "Switchboard upgrades and safety switches",
      "Solar PV and battery electrical connections",
      "Certificates of electrical safety for every job",
      "Clean Energy Council accreditation for solar work",
    ],
  },
  {
    img: PLUMBER_IMG,
    alt: "Animated illustration of a licensed plumber installing a heat pump hot water system",
    icon: Droplets,
    iconColor: "text-wave",
    title: "Licensed Plumbers",
    licence: "Registered with the Building and Plumbing Commission (BPC)",
    copy: "Registered plumbers install and commission our hot water heat pumps to BPC plumbing standards — cold climates tune and all.",
    bullets: [
      "Heat pump hot water supply and installation",
      "Work following BPC plumbing standards",
      "Compliance certificates issued on completion",
      "Photo evidence packaged for rebate claims",
    ],
  },
  {
    img: FRIDGE_IMG,
    alt: "Animated illustration of a refrigeration specialist installing a split system air conditioner",
    icon: Wind,
    iconColor: "text-leaf",
    title: "Refrigeration Specialists",
    licence: "ARC Tick authorised for refrigerant handling",
    copy: "Split systems are refrigeration work — ours hold ARC Tick authorisation and commission every unit to manufacturer spec.",
    bullets: [
      "Split system supply, install and commissioning",
      "Proper vacuum, charge and leak testing",
      "ARC Tick authorisation for refrigerant handling",
      "VEU activity evidence collected on site",
    ],
  },
];

const COMPLIANCE = [
  { icon: Droplets, label: "BPC plumbing standards" },
  { icon: Zap, label: "Energy Safe Victoria electrical safety" },
  { icon: Sun, label: "Solar Victoria program rules" },
  { icon: FileCheck, label: "VEU activity procedures" },
  { icon: ClipboardCheck, label: "Federal STC (SRES) rules" },
  { icon: BadgeCheck, label: "CEC installation standards" },
];

const VETTING = [
  {
    n: "01",
    title: "Licences verified on public registers",
    copy: "Before anyone touches your home, we check every licence on the BPC and Energy Safe Victoria public registers — current, unrestricted, and matching the work.",
  },
  {
    n: "02",
    title: "Insured and inducted",
    copy: "Current public liability insurance, site inductions, and our subcontractor code of conduct: fixed quotes, no pressure, clean worksites.",
  },
  {
    n: "03",
    title: "Accredited for the exact job",
    copy: "Solar and battery installs need Clean Energy Council accreditation. Refrigerant work needs ARC Tick authorisation. No accreditation, no job — simple.",
  },
  {
    n: "04",
    title: "Compliance certificates, always",
    copy: "Plumbing and electrical work comes with the required compliance certificates, and every rebate claim ships with timestamped photo evidence.",
  },
];

export default function InstallersPage() {
  return (
    <div className="pt-28 sm:pt-32" data-testid="installers-page">
      <header className="mx-auto max-w-7xl px-6 pb-14">
        <div className="max-w-3xl">
          <Overline className="text-terra">Our installers</Overline>
          <h1 className="font-display mt-3 text-4xl font-extrabold tracking-tight text-forest-700 sm:text-5xl" data-testid="installers-title">
            The trusted trades behind every install.
          </h1>
          <p className="mt-5 text-base leading-relaxed text-moss sm:text-lg">
            We work with a hand-picked network of licensed electricians,
            plumbers and refrigeration specialists — professionals who follow
            BPC plumbing standards, Energy Safe Victoria requirements, and the
            Solar Victoria, VEU and federal STC program rules on every job.
          </p>
        </div>
      </header>

      <section className="mx-auto max-w-7xl px-6 pb-20" data-testid="installers-trades">
        <div className="grid gap-8 md:grid-cols-3">
          {TRADES.map((t, i) => (
            <FadeUp key={t.title} delay={i * 0.08} className="h-full">
              <div
                className="flex h-full flex-col overflow-hidden rounded-xl border border-forest-700/10 bg-white shadow-sm"
                data-testid={`installer-card-${i}`}
              >
                <div className="overflow-hidden">
                  <img src={t.img} alt={t.alt} loading="lazy" className="aspect-square w-full object-cover transition-transform duration-700 hover:scale-[1.03]" />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-bone-dark">
                    <t.icon className={`h-5 w-5 ${t.iconColor}`} />
                  </span>
                  <h2 className="font-display mt-4 text-xl font-bold text-forest-700">{t.title}</h2>
                  <p className="mt-1.5 text-xs font-bold uppercase tracking-[0.1em] text-terra-dark">{t.licence}</p>
                  <p className="mt-3 text-sm leading-relaxed text-moss">{t.copy}</p>
                  <ul className="mt-4 flex-1 space-y-2">
                    {t.bullets.map((b, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-forest-700">
                        <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-leaf" /> {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="grain bg-forest-800 py-16 text-bone" data-testid="installers-compliance">
        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <FadeUp>
            <Overline className="text-terra">Compliance, not shortcuts</Overline>
            <h2 className="font-display mt-3 max-w-2xl text-2xl font-extrabold sm:text-3xl">
              Every job follows the rulebook — all of it.
            </h2>
          </FadeUp>
          <div className="mt-8 flex flex-wrap gap-3">
            {COMPLIANCE.map(({ icon: Icon, label }, i) => (
              <FadeUp key={label} delay={i * 0.05}>
                <span
                  className="inline-flex items-center gap-2 rounded-full border border-bone/20 bg-forest-900/40 px-4 py-2.5 text-xs font-bold uppercase tracking-[0.1em] text-bone/90"
                  data-testid={`compliance-badge-${i}`}
                >
                  <Icon className="h-4 w-4 text-terra" /> {label}
                </span>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24" data-testid="installers-vetting">
        <SectionHead
          overline="How we vet our crew"
          title="Trusted isn't a feeling. It's a checklist."
        />
        <div className="mt-14 grid gap-10 sm:grid-cols-2">
          {VETTING.map((v, i) => (
            <FadeUp key={v.n} delay={i * 0.06} className="border-t-2 border-forest-700/15 pt-6">
              <p className={`font-display text-3xl font-extrabold ${["text-leaf", "text-wave", "text-terra", "text-leaf"][i]}`}>{v.n}</p>
              <h3 className="font-display mt-3 text-lg font-bold text-forest-700">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-moss">{v.copy}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24" data-testid="installers-cta">
        <div className="grid gap-6 md:grid-cols-2">
          <FadeUp>
            <div className="flex h-full flex-col rounded-xl border border-forest-700/10 bg-bone-dark p-8">
              <ShieldCheck className="h-8 w-8 text-leaf" />
              <h3 className="font-display mt-4 text-xl font-bold text-forest-700">Homeowners</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-moss">
                An accredited, licensed and insured crew — installed to code,
                with every rebate claimed properly.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/book-inspection" data-testid="installers-cta-inspection" className="inline-flex items-center gap-2 rounded-full bg-terra px-6 py-3 text-sm font-bold text-forest-900 transition-colors hover:bg-terra-dark">
                  Book a free inspection <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/rebates" data-testid="installers-cta-rebates" className="inline-flex items-center gap-2 rounded-full border border-forest-700/25 px-6 py-3 text-sm font-bold text-forest-700 transition-colors hover:border-terra hover:text-terra">
                  Check eligibility
                </Link>
              </div>
            </div>
          </FadeUp>
          <FadeUp delay={0.08}>
            <div className="flex h-full flex-col rounded-xl border border-forest-700/10 bg-bone-dark p-8">
              <Mail className="h-8 w-8 text-wave" />
              <h3 className="font-display mt-4 text-xl font-bold text-forest-700">Trades looking to join?</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-moss">
                Licensed, insured and accredited electricians, plumbers and
                refrigeration specialists — send your details and licences for
                a chat about joining our network.
              </p>
              <a
                href="mailto:info@melwestecoenergy.com.au?subject=Subcontractor%20expression%20of%20interest"
                data-testid="installers-cta-join"
                className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-forest-700 px-6 py-3 text-sm font-bold text-bone transition-colors hover:bg-forest-600"
              >
                Express interest by email <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
